/* eslint-disable react-refresh/only-export-components */
// providers/AuthProvider.tsx
import { Onboarding } from "@/components/Onboarding";
import { useOnboarding } from "@/hooks/useOnboarding";
import { api } from "@/services/api";
import { LoginService } from "@/services/auth/login";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

interface User {
  id: number;
  nome: string;
  documento: string;
  email: string;
  telefone?: string;
  publicId: string;
  type: "tutor" | "ong";
  isEmailVerified: boolean;
  isWhatsappVerified?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
  showOnboarding: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const queryClient = useQueryClient();
  const loginService = new LoginService();
  
  const { 
    isOnboardingVisible, 
    isLoading: onboardingLoading,
    completeOnboarding, 
    skipOnboarding,
    setIsOnboardingVisible
  } = useOnboarding();

  const { isLoading: userLoading } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      try {
        const token = localStorage.getItem("vet:token");
        if (!token) return null;

        const userData = await loginService.getUser(token);
        api.defaults.headers["Authorization"] = `Bearer ${token}`;
        setUser(userData);
        return userData;
      } catch {
        return null;
      }
    },
  });

  const login = (userData: User) => {
    setUser(userData);
  };

  const logout = () => {
    setUser(null);
    queryClient.clear();
    api.defaults.headers["Authorization"] = "";
    localStorage.removeItem("vet:token");
  };

  const showOnboarding = () => {
    setIsOnboardingVisible(true);
  };

  const isLoading = userLoading || onboardingLoading;

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#1d71b8]"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        login,
        logout,
        showOnboarding,
      }}
    >
      {user && isOnboardingVisible && (
        <Onboarding 
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
        />
      )}
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};